const GA_ID = "G-BBCEM5KZRT";
const ENABLED = import.meta.env.PROD;

const CONSENT_KEY = "sdd-cookie-consent";
export const OPEN_PREFERENCES_EVENT = "sdd:cookie-preferences";

export type Consent = "granted" | "denied";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: Gtag;
  }
}

function gtag(...args: unknown[]) {
  window.gtag?.(...args);
}

export function getConsent(): Consent | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function setConsent(consent: Consent) {
  try {
    localStorage.setItem(CONSENT_KEY, consent);
  } catch {}

  if (consent === "granted") {
    initAnalytics();
    return;
  }

  if (window.gtag) {
    gtag("consent", "update", { analytics_storage: "denied" });
    clearAnalyticsCookies();
  }
}

export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));
}

function clearAnalyticsCookies() {
  const domains = [window.location.hostname, `.${window.location.hostname}`];

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!name.startsWith("_ga")) return;

    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
    });
    document.cookie = `${name}=; Max-Age=0; path=/`;
  });
}

// GA only loads after the visitor accepts cookies (LGPD)
export function initAnalytics() {
  if (!ENABLED || getConsent() !== "granted") return;

  if (window.gtag) {
    gtag("consent", "update", { analytics_storage: "granted" });
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // gtag.js expects the arguments object, not an array
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("js", new Date());
  // Page views are sent from useSeo so SPA navigations carry the right title
  gtag("config", GA_ID, { send_page_view: false });

  document.addEventListener("click", (event) => {
    if (getConsent() !== "granted") return;

    const link = (event.target as Element | null)?.closest?.("a[href]");
    if (!link) return;

    const href = link.getAttribute("href") ?? "";
    if (href.includes("wa.me/")) {
      gtag("event", "generate_lead", {
        method: "whatsapp",
        page_path: window.location.pathname,
      });
    }
  });

  if (pending) {
    lastPath = null;
    trackPageView(pending.title, pending.language);
  }
}

let lastPath: string | null = null;
let pending: { title: string; language: string } | null = null;

export function trackPageView(title: string, language: string) {
  if (!ENABLED) return;

  // Remember the current page so it can be sent if consent arrives later
  pending = { title, language };
  if (!window.gtag || getConsent() !== "granted") return;

  const path = window.location.pathname + window.location.search;
  if (path === lastPath) return;
  lastPath = path;

  gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: path,
    page_title: title,
    language,
  });
}
